import { useEffect, useState } from 'react';
import { memoize } from 'lodash';
import { isAvailable } from '@splunk/splunk-utils/config';
import fetchSearchBNFs from '@splunk/react-search/utils/searchBNFs';
import { defaultSPLSyntax } from '@splunk/dashboard-utils';

export type SPLSyntax = Record<string, unknown>;

// Only run the fetch once per unique request (parseSyntax = true | false)
// This is exported from the file but not the hooks barrel for testing purposes
export const memoizedFetchBNF = memoize(
    async (parseSyntax: boolean): Promise<SPLSyntax | undefined> => {
        if (isAvailable) {
            return fetchSearchBNFs(parseSyntax);
        }

        return Promise.resolve(undefined);
    }
);

/**
 * Fetch search syntax in Backus-Naur form from Splunk instance and optionally then convert it
 * to a friendlier JSON syntax, else use the default SPL syntax from `@splunk/dashboard-utils`
 * @param {boolean} [arg0.parseSyntax = true] Whether or not to parse the syntax configuration
 * @returns {object} Search BNF for consumption by `@splunk/react-search` components
 */
export const useSearchBNF = ({
    parseSyntax = true,
}: {
    parseSyntax?: boolean;
} = {}): SPLSyntax => {
    const [searchBNF, setSearchBNF] = useState<SPLSyntax>(defaultSPLSyntax);

    useEffect(() => {
        const abortController = new AbortController();
        (async () => {
            try {
                const syntax = await memoizedFetchBNF(parseSyntax);
                if (syntax && !abortController.signal.aborted) {
                    setSearchBNF(syntax);
                }
            } catch {
                // no-op -- this hook will return the default syntax
            }
        })();

        return () => {
            abortController.abort();
        };
    }, [parseSyntax]);

    return searchBNF;
};
